/*
 * TrueNest Pro landing (sections/tn-landing.liquid): before/after slider, savings calculator,
 * double-submit guard on the buy forms. No dependency, loaded with defer.
 * Without this file the page still works: the slider shows the nested kit alone and the
 * calculator shows the default results rendered by Liquid.
 */
(function () {
  'use strict';

  var sliders = [];

  function clamp(value, low, high) {
    return Math.max(low, Math.min(high, value));
  }

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  /* ---------------------------------------------------------------- slider */

  function splitFromEvent(e, el) {
    var r = el.getBoundingClientRect();
    return Math.max(2, Math.min(98, ((e.clientX - r.left) / r.width) * 100));
  }

  function initSlider(el) {
    if (el.getAttribute('data-tnl-ready') === 'true') return;
    el.setAttribute('data-tnl-ready', 'true');

    var range = el.querySelector('.tnl-ba__range');
    var SLOP = 6;
    var dragging = false;
    var waitForMove = false;
    var pointerId = null;
    var startX = 0;
    var startY = 0;

    function set(split) {
      split = clamp(split, 2, 98);
      el.style.setProperty('--tnl-split', split + '%');
      if (range) {
        var rounded = Math.round(split);
        if (Number(range.value) !== rounded) range.value = String(rounded);
        range.setAttribute('aria-valuetext', rounded + ' %');
      }
    }

    function stop() {
      dragging = false;
      waitForMove = false;
      pointerId = null;
    }

    function onDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      /* a second finger never takes over a running drag */
      if (dragging && e.pointerId !== pointerId && !e.isPrimary) return;
      dragging = true;
      pointerId = e.pointerId;
      if (e.pointerType !== 'mouse') {
        /* touch or pen: nothing moves until the gesture is clearly horizontal,
           so a thumb that scrolls the page past the image never moves the line */
        waitForMove = true;
        startX = e.clientX;
        startY = e.clientY;
        return;
      }
      waitForMove = false;
      e.preventDefault();
      /* the drag keeps its events over an iframe or outside the window */
      if (el.setPointerCapture) {
        try { el.setPointerCapture(e.pointerId); } catch (err) { /* pointer already gone */ }
      }
      set(splitFromEvent(e, el));
    }

    function onMove(e) {
      if (!dragging || e.pointerId !== pointerId) return;
      if (waitForMove) {
        var dx = Math.abs(e.clientX - startX);
        var dy = Math.abs(e.clientY - startY);
        if (dx < SLOP && dy < SLOP) return;
        if (dy >= dx) {
          /* vertical gesture: the page scrolls and the line stays where it is */
          stop();
          return;
        }
        waitForMove = false;
      }
      set(splitFromEvent(e, el));
    }

    function onUp(e) {
      if (e && pointerId !== null && e.pointerId !== pointerId) return;
      stop();
    }

    function onRange() {
      set(Number(range.value));
    }

    function onDragStart(e) {
      e.preventDefault();
    }

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('dragstart', onDragStart);
    el.addEventListener('lostpointercapture', onUp);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    if (range) range.addEventListener('input', onRange);

    set(50);

    sliders.push({
      el: el,
      destroy: function () {
        el.removeEventListener('pointerdown', onDown);
        el.removeEventListener('dragstart', onDragStart);
        el.removeEventListener('lostpointercapture', onUp);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        if (range) range.removeEventListener('input', onRange);
        el.removeAttribute('data-tnl-ready');
      }
    });
  }

  /* ------------------------------------------------------------ calculator */

  var fmtCombo = new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2
  });
  var fmtMetres = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  var fmtEuros = new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0
  });
  var fmtField = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false
  });

  /* "30,5", "12.40", "1 234,56 \u20ac" or "1,234.56": the last comma or dot is the decimal mark.
     Letters or an empty field give NaN. */
  function parseAmount(text) {
    var t = String(text == null ? '' : text).replace(/[\s\u00a0\u202f\u20ac]/g, '');
    var mark = Math.max(t.lastIndexOf(','), t.lastIndexOf('.'));
    if (mark >= 0) t = t.slice(0, mark).replace(/[.,]/g, '') + '.' + t.slice(mark + 1);
    if (!/^(\d+\.?\d*|\.\d+)$/.test(t)) return NaN;
    return parseFloat(t);
  }

  function initCalc(root) {
    if (root.getAttribute('data-tnl-ready') === 'true') return;
    var dataEl = root.querySelector('[data-tnl-prices]');
    if (!dataEl) return;
    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (err) {
      return;
    }
    if (!data || !data.films || !data.films.length) return;
    root.setAttribute('data-tnl-ready', 'true');

    var GAIN = Number(root.getAttribute('data-gain')) || 0;
    var PLAN = Number(root.getAttribute('data-plan')) || 0;
    /* another currency than the euro (Shopify markets): the section sets data-cur and data-cur-prefix.
       Whole units, en-US grouping in English and the grouping of the page language otherwise (data-num-locale),
       the same strings as the section prints. In euros nothing changes. */
    var CUR = root.getAttribute('data-cur');
    var PREFIX = root.getAttribute('data-cur-prefix') || '';
    var whole = function (v) { return Math.round(v).toLocaleString(root.getAttribute('data-num-locale') || 'en-US'); };
    var APPROX = String.fromCharCode(0x2248, 0xa0);
    /* euros in the page language: the section sets data-num-locale outside English (de-DE, es-ES...).
       English has no attribute and keeps the fr-FR figures above, exactly as before. */
    var LOC = root.getAttribute('data-num-locale');
    var eur = { combo: fmtCombo, euros: fmtEuros, metres: fmtMetres, field: fmtField };
    if (LOC && LOC !== 'fr-FR') {
      try {
        eur = {
          combo: new Intl.NumberFormat(LOC, { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          euros: new Intl.NumberFormat(LOC, { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }),
          metres: new Intl.NumberFormat(LOC, { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
          field: new Intl.NumberFormat(LOC, { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false })
        };
      } catch (err) { /* unknown locale: keep fr-FR */ }
    }
    var fCombo = CUR ? { format: function (v) { return APPROX + PREFIX + whole(v); } } : eur.combo;
    var fMoney = CUR ? { format: function (v) { return PREFIX + whole(v); } } : eur.euros;
    var fMetres = CUR ? { format: whole } : eur.metres;
    var fField = CUR ? { format: function (v) { return String(Math.round(v * 100) / 100); } } : eur.field;
    var readAmount = CUR ? function (text) { return parseAmount(String(text == null ? '' : text).replace(/[^\d.,]/g, '')); } : parseAmount;

    var filmButtons = root.querySelectorAll('[data-tnl-film]');
    var lamButtons = root.querySelectorAll('[data-tnl-lam]');
    var metresInput = root.querySelector('[data-tnl-metres]');
    var priceInput = root.querySelector('[data-tnl-price]');
    var comboOut = root.querySelector('[data-tnl-combo]');
    var outMetres = root.querySelector('[data-tnl-out="metres"]');
    var outMoney = root.querySelector('[data-tnl-out="money"]');
    var outNet = root.querySelector('[data-tnl-out="net"]');

    var film = 0;
    var lam = 0;
    each(filmButtons, function (b, i) { if (b.getAttribute('aria-pressed') === 'true') film = i; });
    each(lamButtons, function (b, i) { if (b.getAttribute('aria-pressed') === 'true') lam = i; });

    function comboPrice() {
      var f = data.films[film];
      return f && f.prices ? Number(f.prices[lam]) || 0 : 0;
    }

    function press(list, index) {
      each(list, function (b, i) { b.setAttribute('aria-pressed', i === index ? 'true' : 'false'); });
    }

    function calc() {
      var metres = clamp(Number(metresInput ? metresInput.value : 0) || 0, 0, 9999);
      /* an empty or unreadable own price falls back to the example price of the combo */
      var typed = priceInput ? readAmount(priceInput.value) : NaN;
      var price = isFinite(typed) ? clamp(typed, 0, 9999) : comboPrice();
      var savedM = metres * (GAIN / 100);
      var savedE = savedM * price;
      var net = Math.max(0, savedE - PLAN);
      if (outMetres) outMetres.textContent = fMetres.format(savedM);
      if (outMoney) outMoney.textContent = fMoney.format(savedE);
      if (outNet) outNet.textContent = fMoney.format(net);
    }

    function pickCombo() {
      var p = comboPrice();
      if (comboOut) comboOut.textContent = fCombo.format(p);
      if (priceInput) priceInput.value = fField.format(p);
      calc();
    }

    each(filmButtons, function (b, i) {
      b.addEventListener('click', function () {
        film = i;
        press(filmButtons, film);
        pickCombo();
      });
    });

    each(lamButtons, function (b, i) {
      b.addEventListener('click', function () {
        lam = i;
        press(lamButtons, lam);
        pickCombo();
      });
    });

    if (metresInput) {
      metresInput.addEventListener('input', calc);
      metresInput.addEventListener('change', function () {
        /* show the value the results use (0 to 9999) */
        if (metresInput.value === '') return;
        var n = Number(metresInput.value);
        if (!isFinite(n)) return;
        var bounded = clamp(n, 0, 9999);
        if (bounded !== n) {
          metresInput.value = String(bounded);
          calc();
        }
      });
    }
    if (priceInput) {
      priceInput.addEventListener('input', calc);
      priceInput.addEventListener('change', function () {
        /* leaving the field: a readable price is tidied, anything else gets the example price back */
        var n = readAmount(priceInput.value);
        priceInput.value = fField.format(isFinite(n) ? clamp(n, 0, 9999) : comboPrice());
        calc();
      });
    }

    if (comboOut) comboOut.textContent = fCombo.format(comboPrice());
    calc();
  }

  /* ------------------------------------------------------------- buy forms */

  function guardForms(root) {
    each(root.querySelectorAll('form.tnl-buy'), function (form) {
      if (form.getAttribute('data-tnl-ready') === 'true') return;
      form.setAttribute('data-tnl-ready', 'true');
      form.addEventListener('submit', function (e) {
        if (form.getAttribute('data-tnl-sent') === 'true') {
          e.preventDefault();
          return;
        }
        form.setAttribute('data-tnl-sent', 'true');
        var button = form.querySelector('button[type="submit"]');
        if (button) button.setAttribute('aria-busy', 'true');
      });
    });
  }

  window.addEventListener('pageshow', function () {
    each(document.querySelectorAll('form.tnl-buy[data-tnl-sent]'), function (form) {
      form.removeAttribute('data-tnl-sent');
      var button = form.querySelector('button[type="submit"]');
      if (button) button.removeAttribute('aria-busy');
    });
  });

  /* ------------------------------------------------------------------ init */

  function findRoot(scope) {
    if (!scope) return null;
    if (scope.id === 'tn-landing') return scope;
    return scope.querySelector ? scope.querySelector('#tn-landing') : null;
  }

  function init(scope) {
    var root = findRoot(scope);
    if (!root) return;
    each(root.querySelectorAll('[data-tnl-ba]'), initSlider);
    each(root.querySelectorAll('[data-tnl-calc]'), initCalc);
    guardForms(root);
  }

  function destroy(scope) {
    sliders = sliders.filter(function (item) {
      if (scope && scope.contains(item.el)) {
        item.destroy();
        return false;
      }
      return true;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { destroy(e.target); });
})();
